import moment from "moment";
import { positionText, tipModelText } from "./tips";

// Reporte imprimible de UNA liquidación de propinas (A4): bolsas, reparto por
// persona con columna de firma (constancia de entrega) y efecto en el cajón.
// Todo sale de la foto que guarda la liquidación (regla usada y montos), así que
// no cambia aunque después se modifique la configuración. Se imprime desde un
// iframe oculto, como los tickets (utils/ticket.js).

const money = (value) => {
  const amount = Number(value || 0);
  return `${amount < 0 ? "−" : ""}S/ ${Math.abs(amount).toFixed(2)}`;
};
const time = (value) => (value ? moment.parseZone(value).format("DD/MM/YYYY HH:mm") : "—");

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Qué se muestra en "Ponderación" depende de la regla usada.
function weightText(settlement, share) {
  if (settlement.model === "POINTS") {
    const points = Number(share.points);
    return `${points} ${points === 1 ? "pt" : "pts"}`;
  }
  if (settlement.model === "PERCENT") {
    return `${Number((settlement.percents || {})[share.position || "OTRO"] || 0)} % del puesto`;
  }
  if (settlement.model === "INDIVIDUAL") return "Lo de sus mesas";
  return "Partes iguales";
}

export function buildTipsReportHtml({ siteName, session, settlement }) {
  const shares = settlement.shares || [];
  const drawerPaid = shares.reduce(
    (sum, s) =>
      sum +
      Number(s.cash_amount) +
      (settlement.card_payout === "DRAWER" ? Number(s.card_amount) : 0),
    0
  );
  const cashGross = Number(settlement.cash_gross);
  const cardGross = Number(settlement.card_gross);
  const fee = Number(settlement.card_fee);
  const retention = Number(settlement.retention_cash) + Number(settlement.retention_card);
  const distributed = shares.reduce(
    (sum, s) => sum + Number(s.cash_amount) + Number(s.card_amount),
    0
  );
  const payable = shares.filter(
    (s) => settlement.card_payout === "PAYABLE" && Number(s.card_amount) > 0
  );

  const rows = shares
    .map(
      (s) => `<tr>
        <td>${escapeHtml(s.name)}</td>
        <td>${escapeHtml(positionText(s.position))}</td>
        <td>${escapeHtml(weightText(settlement, s))}</td>
        <td class="n">${money(s.cash_amount)}</td>
        <td class="n">${money(s.card_amount)}</td>
        <td class="n"><strong>${money(Number(s.cash_amount) + Number(s.card_amount))}</strong></td>
        <td class="sign"></td>
      </tr>`
    )
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>Liquidación de propinas</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; margin: 0; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 18px 0 6px; border-bottom: 1px solid #999; padding-bottom: 3px; }
  .muted { color: #555; }
  .meta { display: flex; justify-content: space-between; gap: 16px; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #ccc; padding: 5px 6px; text-align: left; vertical-align: middle; }
  th { background: #f0f0f0; font-size: 11px; }
  .n { text-align: right; white-space: nowrap; }
  .sign { width: 28%; height: 30px; }
  .bags td:first-child { width: 70%; }
  .total td { border-top: 2px solid #111; font-weight: 700; }
  .void { color: #c0392b; font-weight: 700; border: 2px solid #c0392b; padding: 6px 10px; margin: 8px 0; text-align: center; }
  tr { break-inside: avoid; }
</style></head><body>
  <h1>Liquidación y distribución de propinas</h1>
  <div class="muted">${escapeHtml(siteName || "iCard")}</div>
  <div class="meta">
    <div>Liquidada: <strong>${time(settlement.created_at)}</strong><br>
      Responsable: <strong>${escapeHtml(settlement.created_by_name || "—")}</strong></div>
    <div>Caja abierta: <strong>${time(session?.opened_at)}</strong>${
      session?.opened_by_name ? ` por ${escapeHtml(session.opened_by_name)}` : ""
    }<br>Regla aplicada: <strong>${escapeHtml(tipModelText(settlement.model))}</strong></div>
  </div>
  ${
    settlement.voided_at
      ? `<div class="void">LIQUIDACIÓN ANULADA — ${time(settlement.voided_at)}${
          settlement.voided_by_name ? ` por ${escapeHtml(settlement.voided_by_name)}` : ""
        }: ${escapeHtml(settlement.void_reason)}</div>`
      : ""
  }

  <h2>1. Fondos a repartir</h2>
  <table class="bags"><tbody>
    <tr><td>Propinas recaudadas en efectivo</td><td class="n">${money(cashGross)}</td></tr>
    <tr><td>Propinas recaudadas en tarjeta</td><td class="n">${money(cardGross)}</td></tr>
    ${
      fee > 0
        ? `<tr><td>(−) Comisión de la tarjeta (${Number(settlement.card_fee_percent)} %)</td><td class="n">${money(-fee)}</td></tr>`
        : ""
    }
    ${
      retention > 0
        ? `<tr><td>(−) Retención del negocio (${Number(settlement.retention_percent)} %)</td><td class="n">${money(-retention)}</td></tr>`
        : ""
    }
    <tr class="total"><td>Total repartido</td><td class="n">${money(distributed)}</td></tr>
  </tbody></table>

  <h2>2. Reparto por persona</h2>
  <table>
    <thead><tr><th>Persona</th><th>Puesto</th><th>Ponderación</th><th class="n">Efectivo</th><th class="n">Tarjeta</th><th class="n">Total</th><th>Firma de conformidad</th></tr></thead>
    <tbody>${rows}
      <tr class="total"><td colspan="3">${shares.length} persona(s)</td>
        <td class="n">${money(shares.reduce((s, x) => s + Number(x.cash_amount), 0))}</td>
        <td class="n">${money(shares.reduce((s, x) => s + Number(x.card_amount), 0))}</td>
        <td class="n">${money(distributed)}</td><td></td></tr>
    </tbody>
  </table>

  <h2>3. Forma de pago y efecto en el cajón</h2>
  <table class="bags"><tbody>
    <tr><td>Forma de pago de la propina de tarjeta</td><td class="n">${
      settlement.card_payout === "DRAWER"
        ? "En efectivo desde la caja"
        : "Por pagar (transferencia o planilla)"
    }</td></tr>
    <tr><td>(+) Propinas en efectivo recibidas en el cajón</td><td class="n">${money(cashGross)}</td></tr>
    <tr><td>(−) Pagos al personal desde la caja (egresos)</td><td class="n">${money(-drawerPaid)}</td></tr>
    <tr class="total"><td>Efecto neto en el efectivo del cajón</td><td class="n">${money(cashGross - drawerPaid)}</td></tr>
  </tbody></table>
  ${
    payable.length
      ? `<p class="muted">Por pagar fuera de la caja: ${payable
          .map(
            (s) =>
              `${escapeHtml(s.name)} ${money(s.card_amount)}${s.card_paid_at ? " (pagada)" : ""}`
          )
          .join(" · ")}.</p>`
      : ""
  }
  <p class="muted" style="margin-top:18px">Documento interno de control; no es un comprobante de pago.</p>
</body></html>`;
}

export function printTipsReport(options) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(buildTipsReportHtml(options));
  doc.close();
  // Esperar a que el iframe termine de pintar antes de imprimir.
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 1000);
  }, 300);
}
